import { roleSchema, userDocumentSchema } from '@util/types/firestore/users';
import { z } from 'zod';

const uidSchema = z.string().optional();

export const getUserArgsSchema = z.object({
  uid: uidSchema,
});
export const createUserDocumentSchema = userDocumentSchema;
export const updateUserRoleArgsSchema = z.object({
  uid: uidSchema,
  newRole: roleSchema,
});
export const updateUserDocArgsSchema = userDocumentSchema.partial();

export const passwordSchema = z
  .string({ required_error: 'password is required' })
  .min(8, 'must be 8 or more characters')
  .regex(/[A-Z]/, 'upper & lowercase required')
  .regex(/[a-z]/, 'upper & lowercase required')
  .regex(/[0-9]/, 'at least one number required');

// Reserved terms that cannot appear in usernames or names (case-insensitive, substring match)
// These prevent impersonation of GearFocus staff, system messages, and common phishing patterns
// EXPORTED: Used by signup forms, dashboard edit, and backend validation
export const RESERVED_TERMS = [
  'gearfocus',
  'gear focus',
  'gearf0cus',
  'gear f0cus',
  'gearbot',
  'admin',
  'administrator',
  'support',
  'official',
  'staff',
  'moderator',
  'helpdesk',
  'customer service',
  'customer',
  'care',
  'notification',
  'system',
  'verify',
  'verification',
  'security',
  'account',
  'help',
  'service',
  'trust',
  'safety',
  'assistant',
  'assistance',
  'billing',
  'payment',
  'refund',
  'team',
  'agent',
  'bot',
  'representative',
  'dispute',
];

/**
 * Normalizes text for validation by handling homoglyphs (lookalike characters)
 * that fraudsters use to bypass username restrictions.
 *
 * Homoglyph replacements:
 * - Capital I (ASCII 73) → lowercase L (common in "HeIper" to bypass "help")
 * - Digit 1 → lowercase i (common in "Adm1n" to bypass "admin")
 * - Digit 0 → lowercase o (common in "Supp0rt" to bypass "support")
 * - Digit 4 → lowercase a (common in "4dmin" to bypass "admin")
 * - Digit 3 → lowercase e (common in "G3arFocus" to bypass "gearfocus")
 *
 * Also normalizes whitespace (trim + collapse multiple spaces to single space)
 *
 * @param text - The text to normalize
 * @returns Normalized lowercase string with homoglyphs replaced
 */
export const normalizeForValidation = (text: string): string => {
  if (!text) return '';

  return text
    .replace(/I/g, 'l')  // Capital I → lowercase L (must be BEFORE toLowerCase)
    .toLowerCase()
    .replace(/1/g, 'i')  // Digit 1 → lowercase i
    .replace(/0/g, 'o')  // Digit 0 → lowercase o
    .replace(/4/g, 'a')  // Digit 4 → lowercase a
    .replace(/3/g, 'e')  // Digit 3 → lowercase e
    .trim()
    .replace(/\s+/g, ' '); // Collapse multiple spaces to single space
};

/**
 * Checks if text contains any reserved terms (case-insensitive, substring match)
 * Uses homoglyph normalization to catch evasion attempts like "HeIper" or "Adm1n"
 * Used for validating usernames, first/last names, and display names
 * @param text - The text to check (username, name, etc.)
 * @returns The blocked term if found, or null if the text is allowed
 */
export const findBlockedTerm = (text: string): string | null => {
  if (!text) return null;
  const normalized = normalizeForValidation(text);
  for (const term of RESERVED_TERMS) {
    if (normalized.includes(term)) {
      return term;
    }
  }
  return null;
};

export const usernameSchema = z
  .string({ required_error: 'username is required' })
  .min(4, 'must be 4 or more characters')
  .regex(/^[A-Za-z0-9\-\'\ ]+$/, 'special characters not allowed')
  .refine(
    (username) => findBlockedTerm(username) === null,
    (username) => ({
      message: `Username cannot contain "${findBlockedTerm(username)}" - this term is reserved`,
    })
  );

/**
 * Schema for validating first name, last name, and display name fields
 * Blocks reserved terms to prevent impersonation (e.g., "Support", "Admin", "GearFocus")
 * Used by signup forms, profile setup, and dashboard edit
 */
export const nameFieldSchema = z
  .string({ required_error: 'Name is required' })
  .min(1, 'Name is required')
  .refine(
    (name) => findBlockedTerm(name) === null,
    (name) => ({
      message: `Name cannot contain "${findBlockedTerm(name)}" - this term is reserved`,
    })
  );

export type CreateUserDocumentArgs = z.infer<typeof createUserDocumentSchema>;
export type GetUserArgs = z.infer<typeof getUserArgsSchema>;
export type UpdateUserRoleArgs = z.infer<typeof updateUserRoleArgsSchema>;
export type UpdateUserDocArgs = z.infer<typeof updateUserDocArgsSchema>;
